/**
 * Streamed saving with the File System Access API (`showSaveFilePicker`),
 * available in Chromium browsers. Elsewhere (Firefox, Safari, WebKit-based app
 * shells) callers fall back to an in-memory Blob and `downloadFile()`.
 */

interface SaveFilePickerWindow {
  showSaveFilePicker?: (options?: { suggestedName?: string }) => Promise<FileSystemFileHandle>;
}

/** A file on disk chosen by the user, open for streaming writes. */
export interface DiskTarget {
  /** Receives the content; closing it (e.g. at the end of `pipeTo`) commits the file. */
  readonly writable: WritableStream<Uint8Array>;
  /**
   * Drop the unfinished write: nothing half-written is committed, and a file
   * the user chose to overwrite keeps its previous content. A file the dialog
   * newly created may stay behind empty — never delete what the user picked,
   * as it may be an existing file.
   */
  discard(): Promise<void>;
}

/**
 * Ask the user where to save `name` and open that file for writing, so large
 * downloads go straight to disk instead of memory. Browsers only show the
 * dialog shortly after a user gesture, so call this from the click (or Enter
 * key) that started the download. Resolves to `'cancelled'` when the user
 * closes the dialog, and to `null` when the browser has no such dialog or
 * refuses it — the caller then downloads in memory instead.
 */
export async function pickDiskTarget(name: string): Promise<DiskTarget | 'cancelled' | null> {
  const host = window as SaveFilePickerWindow;
  if (typeof host.showSaveFilePicker !== 'function') return null;
  let handle: FileSystemFileHandle;
  try {
    handle = await host.showSaveFilePicker({ suggestedName: name });
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') return 'cancelled';
    // No user gesture (any more), a cross-origin frame, another open picker…
    return null;
  }
  let writable: FileSystemWritableFileStream;
  try {
    // Writes go to a temporary copy; only `close()` replaces the file.
    writable = await handle.createWritable();
  } catch {
    return null;
  }
  return {
    writable,
    async discard() {
      // Aborting drops the temporary copy; an already errored stream ignores it.
      await writable.abort().catch(() => undefined);
    },
  };
}
